import { isDev } from '@/lib/env'
import { type AgreementDescriptor, getAgreementDescriptor } from '@/lib/proposal-layout'
import { QuoteStateCode, QuoteType, QuoteWarrantyTerm } from '@/lib/types'

/**
 * April 16, 2025 at 12:00 AM Pacific Time
 */
export const TERMS_AND_CONDITIONS_V2_CUTOFF_DATE = new Date('2025-04-16T08:00:00.000Z')
/**
 * November 5, 2025 at 12:00 AM Pacific Time
 */
export const MASTER_SERVICES_AGREEMENT_CUTOFF_DATE = new Date('2025-11-05T08:00:00.000Z')
/**
 * November 1, 2026 at 12:00 AM Pacific Time. From this moment the plain-language MSA (v5)
 * replaces v3/v4; its Coverage Term follows dsy_warrantyterm. Permanent: signed quotes keep
 * the agreement for their signature time. Mirrored in proposal-api utils/proposal_layout.py.
 */
export const MASTER_SERVICES_AGREEMENT_V5_CUTOFF_DATE = new Date('2026-11-01T07:00:00.000Z')
/**
 * TEMP: dev switches to v5 on October 28, 2026 so it can be proven before launch — remove after
 * November 1, 2026 (the prod cutoff above stays).
 */
export const MASTER_SERVICES_AGREEMENT_V5_DEV_PREVIEW_DATE = new Date('2026-10-28T07:00:00.000Z')

export function agreementVersionForQuote(
  quote:
    | {
        statecode: QuoteStateCode
        dsy_approvalsignaturedatetime: string | null
        dsy_warrantyterm?: QuoteWarrantyTerm | null
      }
    | undefined,
  now = new Date(),
  v5CutoffDate = isDev ? MASTER_SERVICES_AGREEMENT_V5_DEV_PREVIEW_DATE : MASTER_SERVICES_AGREEMENT_V5_CUTOFF_DATE
) {
  const signedAt = quote?.dsy_approvalsignaturedatetime ? new Date(quote.dsy_approvalsignaturedatetime) : null

  if (
    quote?.statecode === QuoteStateCode.Draft ||
    quote?.statecode === QuoteStateCode.Active ||
    (signedAt && signedAt >= MASTER_SERVICES_AGREEMENT_CUTOFF_DATE)
  ) {
    // Signature time first: a signed change order stays Active until the worker wins it,
    // and the acceptance audit records the agreement for the signature time.
    if ((signedAt ?? now) >= v5CutoffDate) return 5
    // One Year selection renders the MSA without §2.5 (v4); otherwise the three-year MSA (v3).
    return quote?.dsy_warrantyterm === QuoteWarrantyTerm.OneYear ? 4 : 3
  }

  if (signedAt && signedAt >= TERMS_AND_CONDITIONS_V2_CUTOFF_DATE) {
    return 2
  }

  return 1
}

type AgreementQuote = {
  statecode: QuoteStateCode
  dsy_type: QuoteType | null
  dsy_approvalsignaturedatetime: string | null
  dsy_warrantyterm?: QuoteWarrantyTerm | null
}

/**
 * The agreement a quote shows and is signed with. From the v5 cutoff a change order carries whatever its
 * project's original quote (the earliest signed quote on the sales order that isn't a change order) was signed
 * with (Max, 2026-10-31). Change orders signed before the cutoff keep the agreement for their own signature.
 * Mirrored in proposal-api utils/proposal_layout.py (get_quote_agreement).
 */
export function agreementForQuote(
  quote: AgreementQuote | undefined,
  originalQuote: AgreementQuote | null | undefined,
  now = new Date(),
  v5CutoffDate = isDev ? MASTER_SERVICES_AGREEMENT_V5_DEV_PREVIEW_DATE : MASTER_SERVICES_AGREEMENT_V5_CUTOFF_DATE
): { agreementVersion: number; agreement: AgreementDescriptor } {
  const signedAt = quote?.dsy_approvalsignaturedatetime ? new Date(quote.dsy_approvalsignaturedatetime) : null
  const original =
    quote?.dsy_type === QuoteType.ChangeOrder && originalQuote && (signedAt ?? now) >= v5CutoffDate
      ? originalQuote
      : null
  const source = original ?? quote
  // The original is signed, so its signature alone picks its agreement, whatever state it is in now
  const agreementVersion = agreementVersionForQuote(
    original ? { ...original, statecode: QuoteStateCode.Won } : quote,
    now,
    v5CutoffDate
  )
  return {
    agreementVersion,
    agreement: getAgreementDescriptor({
      quoteType: source?.dsy_type,
      warrantyTerm: source?.dsy_warrantyterm,
      agreementVersion,
    }),
  }
}
